import { createFileRoute } from "@tanstack/react-router";
import type { ComposerSection } from "@/site/components/composer/composer-sidebar";
import CardComposerPage from "@/site/pages/card-composer";

/**
 * The card customization editor (legacy `/card/send/create?card_send_id=…`). Either
 * resumes an existing draft (`card_send_id`) or starts a new one from a catalog card
 * (`card` = the card_no the Personalize/Send button came from) — in which case the
 * page creates the draft, then replaces the URL with `?card_send_id=…`.
 *
 * `section` mirrors the active step (Customize / Personalize / Preview / Signers / Gift) into
 * the URL so the browser Back/Forward buttons move between steps instead of leaving the flow.
 * Omitted = the card type's first step ("customize" for photo cards, else "personalize"),
 * which keeps the resting URL clean.
 */
type ComposerSearch = {
  card_send_id?: string;
  card?: string;
  /** Reminder-email deep link (birthday_reminder_suggestions "SEND THIS CARD"): tie the
   *  new draft to this reminder and prefill its contact — what the browse page's
   *  pending-reminder stash does when the member comes through /birthday/browse. */
  reminder_id?: string;
  /** BDAY-498: the category the card was started from (old `ccid`); a group category starts
   *  the draft as a group card. */
  ccid?: string;
  section?: ComposerSection;
  /** The Delivery Details takeover is open — in the URL like `section`, so browser Back
   *  closes it instead of leaving the flow. */
  delivery?: boolean;
  /** Checkout completed and a send was expected to resume (BDAY-228). One-shot: the
   *  composer reads it, says its piece, and strips it. Carried in the URL because the
   *  sessionStorage stash can't survive the trip — see the composer's resume effect. */
  paid?: boolean;
  /** The delivery time chosen before checkout (ms epoch), so a lost stash doesn't also
   *  silently reset the member to "send now". Display only — never auto-sends. */
  when?: number;
};

const SECTIONS: readonly ComposerSection[] = [
  "customize",
  "personalize",
  "preview",
  "signers",
  "gift",
];

/**
 * A `card` / `card_id` search value as the card_no (or slug) string. Old mail sits in inboxes
 * quoted-printable-encoded twice, so a click arrives as `?card=3D10144` (`=3D` is QP for `=`):
 * Sentry BDAY-WEB-F, every "Card not found" with a real-looking id. Drop that `3D` in front
 * of a number. A value cut at a QP soft line break (`3D1=`) can't be recovered and still
 * lands on "Nothing to personalize".
 */
function cardParam(v: unknown, shape?: RegExp): string | undefined {
  const raw = typeof v === "number" ? String(v) : typeof v === "string" ? v.trim() : "";
  const value = /^3D\d+$/i.test(raw) ? raw.slice(2) : raw;
  if (!value) return undefined;
  return !shape || shape.test(value) ? value : undefined;
}

export const Route = createFileRoute("/card/send/create")({
  component: CardComposerPage,
  validateSearch: (search: Record<string, unknown>): ComposerSearch => ({
    card_send_id:
      typeof search.card_send_id === "string" && search.card_send_id
        ? search.card_send_id
        : undefined,
    // A DIRECT page load JSON-parses `?card=10197` to a number (in-app navigation keeps
    // the string) — accept both, or external deep-links land on "Nothing to personalize".
    // Legacy reminder-mail suggestion links use `?card_id=<legacy id>` instead
    // (birthday_reminder_suggestions, mailer.py:1889). Legacy card ids ARE our `card_no`
    // for migrated cards, so the alias is a rename, not a lookup.
    card: cardParam(search.card) ?? cardParam(search.card_id, /^\d{1,12}$/),
    reminder_id:
      typeof search.reminder_id === "string" && /^[0-9a-f-]{36}$/i.test(search.reminder_id)
        ? search.reminder_id
        : undefined,
    ccid:
      typeof search.ccid === "string" && /^[0-9a-f-]{36}$/i.test(search.ccid)
        ? search.ccid
        : undefined,
    section: SECTIONS.includes(search.section as ComposerSection)
      ? (search.section as ComposerSection)
      : undefined,
    delivery: search.delivery === true || search.delivery === "true" ? true : undefined,
    // A DIRECT page load JSON-parses these (`?paid=1` arrives as the NUMBER 1, `?when=`
    // as a number) while in-app navigation keeps strings — same trap as `card` above,
    // and these always arrive by direct load, since Stripe's redirect is what sets them.
    paid:
      search.paid === true || search.paid === 1 || search.paid === "1" || search.paid === "true"
        ? true
        : undefined,
    // A time already in the past (old bookmark) is dropped rather than restored as a
    // delivery time the CTA would then refuse.
    when: (() => {
      const ms = typeof search.when === "number" ? search.when : Number(search.when);
      return Number.isFinite(ms) && ms > Date.now() ? ms : undefined;
    })(),
  }),
});
